"use client";

import { useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useZStore } from "@/utils/z-store";
import { WORKSPACE_HEADER } from "@/shared/workspace-constants";
import { useWorkspaces } from "../_hooks/useWorkspaces";

/**
 * Mounted once in the dashboard layout. Loads the caller's workspaces, keeps the
 * `x-workspace-id` request header in sync with the active workspace, and drops
 * the user back to their personal workspace if access is revoked/frozen.
 */
export default function WorkspaceSync() {
  const { data } = useZStore();
  const { fetchWorkspaces } = useWorkspaces();

  // Load workspaces once on mount.
  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  // Mirror the active workspace into the default request header.
  useEffect(() => {
    if (data.activeWorkspaceId) {
      axios.defaults.headers.common[WORKSPACE_HEADER] = data.activeWorkspaceId;
    } else {
      delete axios.defaults.headers.common[WORKSPACE_HEADER];
    }
  }, [data.activeWorkspaceId]);

  // Register the access-revoked/frozen interceptor once. Reads fresh state via
  // getState() so it never closes over stale workspaces.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const code = error?.response?.data?.code;
        if (
          code === "WORKSPACE_ACCESS_REVOKED" ||
          code === "WORKSPACE_FROZEN"
        ) {
          const store = useZStore.getState();
          const personal = (store.data.workspaces || []).find(
            (w) => w.isPersonal,
          );
          if (personal && personal.id !== store.data.activeWorkspaceId) {
            store.actions.setActiveWorkspaceId(personal.id);
            axios.defaults.headers.common[WORKSPACE_HEADER] = personal.id;
            toast(
              code === "WORKSPACE_FROZEN"
                ? "This workspace is frozen — switched to your personal workspace."
                : "Your access to that workspace changed — switched to your personal workspace.",
              { position: "bottom-right" },
            );
          }
        }
        return Promise.reject(error);
      },
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  return null;
}
